// @ts-nocheck

import type { SliceProps as LocationSliceProps } from "@/features/location/locationSlice";

import store from "@/app/store";
import type { Building, Location, Position } from "@/libs/models";
import type { MeasurementSystems } from "../app/constants";
import { RouteType } from "../directions/types";
import type { WhereAmIIndication, WhereAmIOptions } from "../navigation/types";
import type WorkerController from "../worker/WorkerController";
import { type IPartialRoute, WORKER_EVENTS, type WorkerResult } from "./types";

export class WASMRoutesCalculator {
  constructor(
    public building: Building,
    private worker?: WorkerController,
  ) {}

  // TODO: habría que utilizar runWithAbortSignal como wrapper de este método para evitar concurrencia en peticións simultáneas.
  // TODO: revisar outros métodos asíncronos basados en workers
  calculateRoute(
    origin: number | Position | null | undefined,
    destination: number | Position | null | undefined,
    routeType: RouteType | null,
    optionalParams?: {
      includedTags?: string[];
      excludedTags?: string[];
    },
  ): Promise<WorkerResult<IPartialRoute>> {
    const indicationsParameters = this.getIndicationsParameters();
    const includedTags =
      optionalParams?.includedTags ??
      store.getState().config.directions.includedTags ??
      [];
    const excludedTags =
      optionalParams?.excludedTags ??
      store.getState().config.directions.excludedTags ??
      [];
    const floorMap = this.building.floors.reduce((acc, f) => {
      acc[f.id] = f.name;
      return acc;
    }, {});
    return this.worker?.postMessageToWorker({
      payload: {
        destination,
        excludedTags,
        floorMap,
        includedTags,
        indicationsParameters,
        origin,
        routeType,
      },
      type: WORKER_EVENTS.DIRECTIONS,
    });
  }

  distanceToPois(payload: {
    location: Location;
    includeTag: string[];
    excludeTag: string[];
    thresholdPoiDistances: number;
    poiIds: Poi["id"][];
  }): Promise<WorkerResult<Record<string, number>>> {
    return this.worker?.postMessageToWorker({
      payload,
      type: WORKER_EVENTS.DISTANCES,
    });
  }

  // TODO: type result
  calculateNativeNavigation(): Promise<WorkerResult<any>> {
    const currentLocation = store.getState().location.currentLocation;
    const currentMeasurementSystem =
      store.getState().config.i18n.measurementSystem;
    const floorMap = this.building?.floors?.reduce((acc, f) => {
      acc[f.id] = f.getPrintName();
      return acc;
    }, {});
    const actions = store.getState().config.actions.actions;
    const lang = store.getState().config.i18n.language;
    const poiMap = this.building?.pois?.toArray().reduce((acc, p) => {
      acc[p.id] = p.getTranslatedProperty("name");
      return acc;
    }, {});
    return this.worker?.postMessageToWorker({
      payload: {
        actions,
        currentLocation,
        currentMeasurementSystem,
        floorMap,
        lang,
        poiMap,
      },
      type: WORKER_EVENTS.NAVIGATION,
    });
  }

  whereAmI(
    currentLocation: LocationSliceProps["currentLocation"] | undefined,
    options?: WhereAmIOptions,
    currentMeasurementSystem?: MeasurementSystems,
  ): Promise<WorkerResult<WhereAmIIndication>> {
    if (!currentLocation) return;

    const routeConfiguration = {
      type: RouteType.CHOOSE_SHORTEST,
    } as any;
    const floorMap = this.building?.floors?.reduce((acc, f) => {
      acc[f.id] = f.getPrintName();
      return acc;
    }, {});
    const poiMap = this.building?.pois?.toArray().reduce((acc, p) => {
      acc[p.id] = p.getTranslatedProperty("name");
      return acc;
    }, {});

    const lang = store.getState().config.i18n.language;
    const thresholdPoiDistances =
      store.getState().config.directions.thresholdPoiDistances;

    return this.worker?.postMessageToWorker({
      payload: {
        currentLocation,
        currentMeasurementSystem,
        floorMap,
        language: lang,
        options,
        poiMap,
        rotation: this.building.geometry.rotation,
        routeConfiguration,
        thresholdPoiDistances,
      },
      type: WORKER_EVENTS.WHERE_AM_I,
    });
  }

  getIndicationsParameters() {
    const radiusPoiIndication = store
      .getState()
      .config.directions.radiusPoiIndication?.toString();
    const turnThreshold = store
      .getState()
      .config.directions.turnThreshold?.toString();
    const turnBackThreshold = store
      .getState()
      .config.directions.turnBackThreshold?.toString();

    const distanceToDiscriminateLookToTurn = store
      .getState()
      .config.directions.distanceToDiscriminateLookToTurn?.toString();

    const slightTurnThreshold = store
      .getState()
      .config.directions.slightTurnThreshold?.toString();

    const indicationsParameters = {
      distanceToDiscriminateLookToTurn: parseFloat(
        distanceToDiscriminateLookToTurn ?? "",
      ),
      hasBearingOrigin:
        store.getState().location.currentLocation?.hasBearing ?? null,
      radiusPoiIndication: parseFloat(radiusPoiIndication ?? ""),
      segmentsDistanceThreshold:
        store.getState().config.navigation.distanceToIgnoreIndication,
      slightTurnThreshold: parseFloat(slightTurnThreshold ?? ""),
      turnBackThreshold: parseFloat(turnBackThreshold ?? ""),
      turnThreshold: parseFloat(turnThreshold ?? ""),
      yawOrigin: parseFloat(
        store.getState().location.currentLocation?.bearing ?? null,
      ),
    };

    return JSON.stringify({
      indicationsParameters: [indicationsParameters],
    });
  }

  setNavigationParametersRoute(): Promise<WorkerResult<void>> {
    const distanceToGoalThresholdString = store
      .getState()
      .config.navigation.distanceToGoalThreshold?.toString();

    const timeToUpdateNextIndicationString = store
      .getState()
      .config.navigation.timeToUpdateNextIndication?.toString();

    const distanceToChangeIndicationThresholdString = store
      .getState()
      .config.navigation.distanceToChangeIndicationThreshold?.toString();
    const outsideRouteThresholdString = store
      .getState()
      .config.navigation.outsideRouteThreshold?.toString();

    const outsideRouteThresholdOnIncorrectFloorString = store
      .getState()
      .config.navigation.outsideRouteThresholdOnIncorrectFloor?.toString();

    const timeToIgnoreUnexpectedFloorChanges = store
      .getState()
      .config.navigation.timeToIgnoreUnexpectedFloorChanges?.toString();

    const navigationParameters = {
      distanceToChangeIndicationThreshold: parseFloat(
        distanceToChangeIndicationThresholdString ?? "",
      ),
      distanceToGoalThreshold: parseFloat(distanceToGoalThresholdString ?? ""),
      outsideRouteThreshold: parseFloat(outsideRouteThresholdString ?? ""),
      outsideRouteThresholdOnIncorrectFloor: parseFloat(
        outsideRouteThresholdOnIncorrectFloorString ?? "",
      ),
      timeToKeepFloorChangeIndication: parseFloat(
        timeToIgnoreUnexpectedFloorChanges ?? "",
      ),
      timeToUpdateNextIndication: parseFloat(
        timeToUpdateNextIndicationString ?? "",
      ),
    };

    return this.worker?.postMessageToWorker({
      payload: navigationParameters,
      type: WORKER_EVENTS.NAVIGATION_PARAMETERS,
    });
  }

  setWorker(w: WorkerController) {
    this.worker = w;
  }
}
